import axiosHandler from '@/utils/axios-handler';
import { AxiosResponse } from 'axios';
import { academyApiHeaders, academyApiUrl } from '@/lib/constants/api';

export interface QuizSubmitRequest {
  formData: Array<{
    question_uid: string;
    selected_options: boolean | string | string[];
  }>;
  lessonUid: string;
  courseUid: string;
}

export interface AssessmentSubmitRequest {
  formData: Array<{
    question_uid: string;
    selected_options: boolean | string | string[];
  }>;
  LearningPathUid: string;
}

export interface QuizResultItem {
  questionUid: string;
  result: boolean;
  selectedOptions: string | string[] | boolean;
  correctAnswer: string | string[] | boolean;
}

export interface QuizResult {
  percentageAchieved: number;
  totalMarks: number;
  totalQuestions: number;
  totalAttempts?: number;
  attemptNumber?: number;
  results?: QuizResultItem[];
  attemptLog?: unknown[];
}

export interface AssessmentResult {
  percentageAchieved?: number;
  maxPercentageAchieved?: number;
  totalMarks?: number;
  totalQuestions?: number;
  attemptLog?: Array<{
    percentageAchieved: number;
    totalMarks: number;
    totalQuestions: number;
  }>;
  totalAttempts?: number;
  lastAttemptedAt?: string;
  topicWiseResults?: Array<{
    topic: string;
    correct: number;
    total: number;
  }>;
  badgeData?: {
    uid: string;
    badgeReferenceUid: string;
  };
}

/**
 * Submit quiz answers for a lesson
 */
export async function QuizSubmit({
  formData,
  lessonUid,
  courseUid,
}: QuizSubmitRequest): Promise<QuizResult> {
  try {
    const url = academyApiUrl(`/v1/users/courses/${courseUid}/lessons/${lessonUid}`);

    const response = await axiosHandler({
      method: 'PUT',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        quizAnswers: formData,
      },
      withCredentials: true,
    });

    if ('status' in response && (response.status === 200 || response.status === 201) && 'data' in response) {
      const data = (response as AxiosResponse).data;
      const quizResult = data.quizResult || {};
      const assessment = data.progress?.assessment ?? data.assessment;
      const attemptLog = assessment?.attemptLog ?? [];

      const result: QuizResult = {
        percentageAchieved: quizResult.percentageAchieved ?? 0,
        totalMarks: quizResult.totalMarks ?? 0,
        totalQuestions: quizResult.totalQuestions ?? 0,
        totalAttempts: assessment?.totalAttempts ?? attemptLog.length,
        attemptNumber: attemptLog?.at(-1)?.attemptNumber ?? attemptLog.length,
        results: quizResult.results,
        attemptLog: Array.isArray(attemptLog) ? attemptLog : [],
      };
      return result;
    } else {
      const errorMessage = 'status' in response 
        ? `Quiz submission failed with status ${response.status}`
        : 'Quiz submission failed: Invalid response';
      console.error(errorMessage, response);
      throw new Error(errorMessage);
    }
  } catch (error: any) {
    console.error('Error submitting quiz:', error);

    let errorMessage = 'Failed to submit quiz';

    if (error?.response?.data) {
      errorMessage = error.response.data.message || error.response.data.error || errorMessage;

      if (error.response.status === 400) {
        if (errorMessage.includes('Learning Material not completed') || errorMessage.includes('Learning material not completed')) {
          errorMessage = 'Learning Material not completed';
        }
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
}

/**
 * Submit assessment answers for a learning path
 */
export async function AssessmentSubmit({
  formData,
  LearningPathUid,
}: AssessmentSubmitRequest): Promise<AssessmentResult> {
  try {
    const url = academyApiUrl(`/v1/users/learning-paths/${LearningPathUid}`);

    const response = await axiosHandler({
      method: 'PUT',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        assessmentAnswers: formData,
      },
      withCredentials: true,
    });

    if ('status' in response && (response.status === 200 || response.status === 201) && 'data' in response) {
      const data = (response as AxiosResponse).data;

      const result: AssessmentResult = {
        ...data.assessmentResult,
        attemptLog: data.progress?.assessment?.attemptLog,
        totalAttempts: data.progress?.assessment?.totalAttempts,
        badgeData: data?.userBadgeData,
      };
      return result;
    } else {
      const errorMessage = 'status' in response 
        ? `Assessment submission failed with status ${response.status}`
        : 'Assessment submission failed: Invalid response';
      console.error(errorMessage, response);
      throw new Error(errorMessage);
    }
  } catch (error: any) {
    console.error('Error submitting assessment:', error);

    let errorMessage = 'Failed to submit assessment';

    if (error?.response?.data) {
      errorMessage = error.response.data.message || error.response.data.error || errorMessage;

      if (error.response.status === 400) {
        if (errorMessage.includes('Learning Material not completed') || errorMessage.includes('Learning material not completed')) {
          errorMessage = 'Learning Material not completed';
        }
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
}

/**
 * Gets assessment data for a learning path. Raw `fetch()` here (not axiosHandler)
 * is deliberate — see CLAUDE.md. Also syncs isLearningMaterialCompleted to true when
 * all courses hit 100%, in case the backend still gates questions on that flag.
 */
export async function getAssessmentData(LearningPathUid: string): Promise<any[]> {
  const { fetchLearningPathProgress } = await import('@/lib/api/get-user-learning-paths');
  const { handleLearningPath } = await import('@/lib/api/update-user-activity');

  try {
    const progress = await fetchLearningPathProgress(LearningPathUid);
    const courses = progress?.courses ?? [];
    const lpProgress = progress?.progress;
    const allCourses100 =
      courses.length > 0 &&
      courses.every((c: { progress?: { percentage?: number } }) => (c.progress?.percentage ?? 0) >= 100);
    const needsSync =
      allCourses100 &&
      lpProgress?.isLearningMaterialCompleted === false;

    if (needsSync) {
      try {
        await handleLearningPath({
          learningPathUid: LearningPathUid,
          learningPathActivity: { progress: { isLearningMaterialCompleted: true } },
        });
      } catch {
        // Backend may not accept or may compute server-side; continue to fetch
      }
    }

    const response = await fetch(
      academyApiUrl(`/v1/questionnaire/learning_path/get_assessment/${LearningPathUid}`),
      {
        method: 'GET',
        credentials: 'include',
        headers: academyApiHeaders(),
      }
    );
    const data = await response.json();
    const questions = data?.questions ?? data?.data?.questions ?? [];
    if (response.status !== 200 || !Array.isArray(questions) || questions.length === 0) {
      throw new Error(data?.message || 'Failed to fetch assessment data');
    }
    return questions;
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : 'Failed to fetch assessment data';
    throw new Error(message);
  }
}

/**
 * Filter assessment questions based on topic weightage
 */
export function filterAssessmentQuestions(assessmentData: {
  assessment?: any[];
  topic_weightage?: Record<string, number>;
  question_count?: number;
}): any[] {
  if (
    !assessmentData ||
    !assessmentData.assessment ||
    !assessmentData.topic_weightage
  ) {
    return [];
  }

  const validTopics = Object.entries(assessmentData.topic_weightage)
    .filter(([_, weight]) => weight && weight > 0)
    .map(([topic, weight]) => ({ topic, weight }));

  const totalWeight = validTopics.reduce((sum, { weight }) => sum + weight, 0);
  const totalQuestions =
    assessmentData.question_count || assessmentData.assessment.length;

  // Bucket questions by topic
  const questionsByTopic = validTopics.reduce((acc, { topic }) => {
    acc[topic] = assessmentData.assessment!.filter((question) => {
      const questionType = Object.keys(question)[0];
      return question[questionType].topic === topic;
    });
    return acc;
  }, {} as Record<string, any[]>);

  // Pick a weighted, random share of questions per topic
  const filteredQuestions: any[] = [];
  validTopics.forEach(({ topic, weight }) => {
    const topicQuestions = questionsByTopic[topic];
    const questionCount = Math.round((weight / totalWeight) * totalQuestions);
    const selectedQuestions = topicQuestions
      .sort(() => Math.random() - 0.5)
      .slice(0, questionCount);

    filteredQuestions.push(...selectedQuestions);
  });

  // Shuffle across topics and cap at the requested total
  const finalQuestions = filteredQuestions.sort(() => Math.random() - 0.5);
  return finalQuestions.slice(0, totalQuestions);
}

